import { useCallback, useEffect, useMemo } from 'react'
import gql from 'graphql-tag'
import { useGqlUseActivityFilterSlidesLazyQuery } from 'generated/graphql'
import { FILTER_LABEL_BY_ACTIVITY_TYPE_ID } from '@app.feature/activityFilter/modules/constant'
import { ActivityTypeID } from 'constants/enums'
import useFilterItem from '../../../hooks/useFilterItem'
import useActivityFilterStore, {
  TActivityFilterItem,
} from '../../../store/storeActivityFilter'

gql`
  fragment useActivityFilterSlides_activityFilterItemById on ActivityFilterItemByID {
    dataId: id
    label: name
  }

  fragment useActivityFilterSlides_ActivityFitlerItemByRange on ActivityFitlerItemByRange {
    label: name
    from
    to
  }

  query gqlUseActivityFilterSlides($input: ActivityFilterListInput!) {
    activityFilterList(input: $input) {
      isCanDuplicate: duplicatable
      name: type
      items {
        ... on ActivityFilterItemByID {
          ...useActivityFilterSlides_activityFilterItemById
        }
        ... on ActivityFitlerItemByRange {
          ...useActivityFilterSlides_ActivityFitlerItemByRange
        }
      }
    }
  }
`

const PARSING_FILTER_TYPE = {
  organizationTypes: 'orgTypeIDs',
  internTypes: 'internTypeIds',
}

export const useActivityFilterSlides = () => {
  const { filterItemIsSelected } = useFilterItem()
  const [fetch, { data, loading }] = useGqlUseActivityFilterSlidesLazyQuery()
  const activityType = useActivityFilterStore((state) => state.activityType)
  const addSelectedFilter = useActivityFilterStore(
    (state) => state.addSelectedFilter,
  )
  const removeSelectedFilter = useActivityFilterStore(
    (state) => state.removeSelectedFilter,
  )
  const updateSelectedFilter = useActivityFilterStore(
    (state) => state.updateSelectedFilter,
  )

  const activityTypeIsRecruit =
    activityType === ActivityTypeID.INTERN ||
    activityType === ActivityTypeID.RECRUIT

  const filterList = useMemo(() => {
    return data?.activityFilterList
      ?.map((list) => ({
        ...list,
        // data result organizationTypes name is deprecated.
        // to use orgTypeIds
        name: PARSING_FILTER_TYPE[list.name]
          ? PARSING_FILTER_TYPE[list.name]
          : list.name,
      }))
      ?.filter((filterList) =>
        activityTypeIsRecruit ? filterList.name !== 'categoryIDs' : true,
      ) // 직무 카테고리는 별도 필드로 관리
      .map((filter) => ({
        ...filter,
        items: filter.items?.map(
          (item): TActivityFilterItem => ({
            ...item,
            id:
              item?.__typename === 'ActivityFilterItemByID'
                ? item.dataId
                : item?.label || '',
            label: item?.label || '',
          }),
        ),
      }))
  }, [data?.activityFilterList, activityTypeIsRecruit])

  const getFilterLabel = useCallback(
    (filterType: string) => {
      const result =
        FILTER_LABEL_BY_ACTIVITY_TYPE_ID[activityType]?.[filterType] || ''
      return result
    },
    [activityType],
  )

  const handleChange = ({ name, filterItem, isCanDuplicate }) => {
    if (!isCanDuplicate) {
      return updateSelectedFilter({
        name,
        filters: [filterItem],
      })
    }

    const isSelect = filterItemIsSelected({ name, filterItemId: filterItem.id })

    if (isSelect) {
      return removeSelectedFilter({
        filterId: filterItem.id,
        name,
      })
    }

    addSelectedFilter({
      name,
      filter: filterItem,
    })
  }

  useEffect(() => {
    fetch({
      variables: {
        input: {
          activityTypeID: activityType,
        },
      },
    })
  }, [fetch, activityType])

  return {
    filterList,
    getFilterLabel,
    onChange: handleChange,
    isLoading: loading || !data,
  }
}
